import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { ActiveCartFacade, Cart, OrderEntry, PromotionLocation, PromotionResult } from '@spartacus/cart/base/root'
import { CmsService, PageType, RoutingService } from '@spartacus/core'
import { CurrentProductService, ICON_TYPE, LaunchDialogService } from '@spartacus/storefront'
import { FileSaverService } from 'ngx-filesaver'
import { Observable, of, Subscription } from 'rxjs'
import { filter, finalize, map, mergeMap, shareReplay, startWith, switchMap, switchMapTo, tap } from 'rxjs/operators'
import { CustomCartService } from '../../../../services/custom-cart.service'
import { FileService } from '../../../../services/file.service'
import { SharedService } from '../../../../services/shared/shared.service'
import { CustomLaunchDialogService } from './custom-launch-dialog.service'

@Component({
    selector: 'app-custom-added-to-cart-dialog',
    templateUrl: './custom-added-to-cart-dialog.component.html',
    styleUrls: ['./custom-added-to-cart-dialog.component.scss'],
    standalone: false
})
export class CustomAddedToCartDialogComponent implements OnInit, OnDestroy {
  iconTypes = ICON_TYPE

  entry$: Observable<OrderEntry>

  cart$: Observable<Cart>
  loaded$: Observable<boolean>
  //  loaded$: Observable<boolean> = this.activeCartFacade.isStable();

  addedEntryWasMerged$: Observable<boolean>
  /**
   *
   * @deprecated since 3.0, set numberOfEntriesBeforeAdd instead
   */
  increment: boolean
  numberOfEntriesBeforeAdd: number
  orderPromotions$: Observable<PromotionResult[]>
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart
  uomCode: string
  uomName: string
  hasStock?: boolean
  maxQuantity?: number
  quantity = 0
  modalIsOpen = false

  @ViewChild('dialog', { read: ElementRef })
  dialog: ElementRef

  form: FormGroup = new FormGroup({})

  private quantityControl$: Observable<FormControl>
  public showSpinner = false
  public showExportError = false
  public exportErrorMsg: string
  public subscription = new Subscription()

  constructor(
    protected activeCartFacade: ActiveCartFacade,
    protected modalService: LaunchDialogService,
    protected cartService: ActiveCartFacade,
    public customCartService: CustomCartService,
    private fileService: FileService,
    private fileSaverService: FileSaverService,
    public sharedService: SharedService,
    public customLaunchDialogService: CustomLaunchDialogService,
    private cmsService: CmsService,
    private currentProductService: CurrentProductService,
    private routingService: RoutingService
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  // protected promotionService: PromotionService,
  /**
   * Returns an observable formControl with the quantity of the cartEntry,
   * but also updates the entry in case of a changed value.
   * The quantity can be set to zero in order to remove the entry.
   */

  getQuantityControl(): Observable<FormControl> {
    if (!this.quantityControl$) {
      const dataentry = this.sharedService.dialogs
      this.entry$ = dataentry.entry$

      this.quantityControl$ = this.entry$.pipe(
        filter((e) => !!e),
        map((entry) => this.getFormControl(entry)),
        switchMap(() =>
          this.form.valueChanges.pipe(
            // tslint:disable-next-line:deprecation
            // startWith(null),
            startWith(''),
            tap((valueChange) => {
              if (valueChange) {
                this.cartService.updateEntry(valueChange.entryNumber, valueChange.quantity)
                if (valueChange.quantity === 0) {
                  this.dismissModal('Removed')
                }
              } else {
                this.form.markAsPristine()
              }
            })
          )
        ),
        map(() => <FormControl>this.form.get('quantity')),
        shareReplay({ bufferSize: 1, refCount: true })
      )
    }
    return this.quantityControl$
  }

  ngOnInit() {
    const cart = this.sharedService.dialogs
    this.cart$ = cart.cart$

    const dataPopup = this.sharedService.dialogs
    this.loaded$ = dataPopup.loaded$

    this.addedEntryWasMerged$ = this.loaded$.pipe(
      filter((loaded) => loaded),
      switchMapTo(this.cartService.getEntries()),
      map((entries) => entries.length === this.numberOfEntriesBeforeAdd)
    )

    this.getQuantityControl()
  }

  private getFormControl(entry: OrderEntry): FormControl {
    if (!this.form.get('quantity')) {
      const quantity = new FormControl(entry.quantity, { updateOn: 'blur' })
      this.form.addControl('quantity', quantity)

      const entryNumber = new FormControl(entry.entryNumber)
      this.form.addControl('entryNumber', entryNumber)
    }
    return <FormControl>this.form.get('quantity')
  }

  dismissModal(reason?: any): void {
    this.modalService.closeDialog(reason)
  }

  public exportCart(cartCode: string): void {
    this.showSpinner = true
    this.customCartService
      .exportCart(cartCode)
      .pipe(
        finalize(() => {
          this.showSpinner = false
          setTimeout(() => {
            this.showExportError = false
          }, 2000)
        })
      )
      .subscribe(
        (res) => {
          const fileType = this.fileService.mimeConvertor('text/csv')
          const fileName = 'CSV Cart ' + cartCode + fileType
          this.fileSaverService.saveText(res, fileName)
        },
        (err) => {
          this.showExportError = true
          this.exportErrorMsg = err.message
        }
      )
  }

  /**
   * ECS-512
   * If the user is on a product details page, fetch the product base category and navigate to category.
   * If the product base category doesn't exist, simply close the modal.
   * If the user is on a product list page, simply close the modal.
   */
  public redirectToProductsPage(): void {
    const currentPageSubscription = this.cmsService
      .getCurrentPage()
      .pipe(
        mergeMap((page) =>
          page.type === PageType.PRODUCT_PAGE ? this.currentProductService.getProduct() : of(undefined)
        )
      )
      .subscribe((pdpProduct) => {
        if (!pdpProduct) {
          this.customLaunchDialogService.closeDialog('close')
        } else {
          const currentProductCategoryUrl = pdpProduct.categories?.find((category) =>
            category.code.includes('category')
          )?.url

          if (currentProductCategoryUrl) {
            this.routingService.go(currentProductCategoryUrl)
          } else {
            this.customLaunchDialogService.closeDialog('close')
          }
        }
      })

    this.subscription.add(currentPageSubscription)
  }
}
